if (!customElements.get("quick-add-modal")) {

  customElements.whenDefined('modal-dialog').then(() => {

  // ── Module-level singletons ─────────────────────────────────────────────────
  // Reused across every modal open — avoids recreating per call.
  const _qvCache   = new Map(); // url → processed innerHTML string
  const _domParser = new DOMParser();

  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        // Bound once so add/removeEventListener are symmetric (#560)
        this._qvFocusTrap = (e) => {
          if (e.key !== 'Tab') return;
          const focusable = Array.from(this.querySelectorAll(
            'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
          )).filter(el => el.offsetParent !== null);
          if (!focusable.length) { e.preventDefault(); return; }
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (!this.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
      }

      hide(preventFocus = false) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);

        const isFromPromoDrawer = this.openedBy && this.openedBy.closest('#promo-drawer');

        this.modalContent.innerHTML = "";
        window.pauseAllMedia();
        if (preventFocus) this.openedBy = null;
        document.removeEventListener('keydown', this._qvFocusTrap);
        super.hide();

        if (isFromPromoDrawer) {
          document.getElementById('promo-drawer')?.classList.add('promo-drawer--open');
        }
      }

      show(opener) {
        const promoDrawer = opener.closest('#promo-drawer');
        if (promoDrawer) {
          promoDrawer.classList.remove('promo-drawer--open');
        }

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        // Cache spinner ref — used in both .then() and .finally()
        const spinner = opener.querySelector(".loading-overlay__spinner");
        spinner?.classList.remove("hidden");

        const url = opener.getAttribute("data-product-url");

        // ── Shared content-apply logic (cache hit and cache miss) ─────────────
        const applyContent = (html) => {
          this.setInnerHTML(this.modalContent, html);

          if (typeof window.handleAddLimitedEdition === 'function') window.handleAddLimitedEdition();

          // starapps: tag product info wrapper with URL for downstream use
          this.querySelector(".product__info-wrapper")
            ?.setAttribute("data-product-url", url);

          if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
          document.addEventListener('keydown', this._qvFocusTrap);
          window.dispatchEvent(new Event("quickview:load"));
        };

        // ── Abort any in-flight fetch for this modal ──────────────────────────
        this._fetchAbort?.abort();
        this._fetchAbort = new AbortController();

        if (_qvCache.has(url)) {
          // Cache hit — no network, no parse, no DOM transforms
          applyContent(_qvCache.get(url));
          opener.removeAttribute("aria-disabled");
          opener.classList.remove("loading");
          spinner?.classList.add("hidden");
          this._initSwiper();
          return;
        }

        fetch(url, { signal: this._fetchAbort.signal })
          .then(r => r.text())
          .then(responseText => {
            this.productElement = _domParser
              .parseFromString(responseText, "text/html")
              .querySelector('section[id^="MainProduct-"]');

            if (!this.productElement) return;

            this.preventDuplicatedIDs();
            this.removeDOMElements();

            // Cache processed innerHTML — subsequent opens skip fetch + parse
            _qvCache.set(url, this.productElement.innerHTML);

            applyContent(this.productElement.innerHTML);
          })
          .catch(e => { if (e.name !== 'AbortError') console.error(e); })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            spinner?.classList.add("hidden");
            this._initSwiper();
          });
      }

      // ── Swiper ──────────────────────────────────────────────────────────────
      _initSwiper() {
        const swiperEl = this.querySelector('.js-media-list');
        if (!swiperEl || typeof Swiper === 'undefined') return;
        swiperEl.swiper?.destroy(true, true);
        new Swiper(swiperEl, {
          slidesPerView: 1,
          navigation: {
            nextEl: this.querySelector(".swiper-btn--next"),
            prevEl: this.querySelector(".swiper-btn--prev"),
          },
        });
      }

      // ── DOM helpers ─────────────────────────────────────────────────────────

      setInnerHTML(element, html) {
        element.innerHTML = html;
        // Re-inject script tags so they execute (innerHTML doesn't run scripts)
        element.querySelectorAll("script").forEach(old => {
          const next = document.createElement("script");
          for (const { name, value } of old.attributes) next.setAttribute(name, value);
          next.textContent = old.textContent;
          old.replaceWith(next);
        });
      }

      removeDOMElements() {
        // Single querySelectorAll pass instead of three separate queries
        this.productElement
          .querySelectorAll("pickup-availability, product-modal, .customer")
          .forEach(el => el.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach(el => { el.dataset.originalSection = sectionId; });
      }

      preventVariantURLSwitching() {
        // Cache querySelector result — used twice
        const el = this.modalContent.querySelector("variant-radios,variant-selects");
        if (el) el.setAttribute("data-update-url", "false");
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;
        galleryList.setAttribute("role", "presentation");
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute("role", "presentation"));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector(".product");
        if (!product?.classList.contains("product--columns")) return;

        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        // Lookup avoids repeated string .replace() calls
        const sizeMap = { 'product--medium': '605px', 'product--small': '495px' };
        const override = Object.keys(sizeMap).find(c => product.classList.contains(c));
        const sizes = `(min-width: 1000px) ${sizeMap[override] || '715px'}, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)`;

        mediaImages.forEach(img => img.setAttribute("sizes", sizes));
      }
    }
  );

  }); // end whenDefined

}
